{ceil, floor, max, min, round} := Math
{assign} := Object

{
  Container
  Graphics
  ObservablePoint
  Sprite
  Text
  Texture
} from "pixi.js"

{ MidiEvent, clamp } from @danielx/lib

{
  PIXELS_PER_BEAT
  CHANNEL_COLORS
  FINGERING_COLORS
  JUDGMENT_COLORS
  JUDGMENT_FEEDBACK_MS
  JUDGMENT_LABELS
  ROLL_BG_COLOR
} from ./const.civet
{ fingeringDisplay } from ./lib/midi/practice-metadata.civet
{ BURST_MAX_AGE_MS, BurstLayer, TextSpritePool, comboGlow, spawnJudgedBurst } from ./note-fx.civet
{ JudgmentFx } from ./arcade-fx.civet
{ rollInteraction } from ./roll-interaction.civet
{ noteName } from ./lib/util.civet

type MidiSong = import('./lib/midi/song.civet').default

// DAW-style horizontal roll: pitch runs up the screen in uniform semitone
// lanes, time scrolls right-to-left through a hit line at the right edge of
// a keyboard drawn down the left side. The vertical roll (piano-roll.civet)
// is this view's sibling; both share the staff view's beat scale
// (PIXELS_PER_BEAT) and channel colors.

BLACK_PCS := [1, 3, 6, 8, 10]

isBlack := (n: number) ->
  BLACK_PCS.includes n % 12

// Default lane span when the song has no notes: C3..B5
DEFAULT_LOW := 48
DEFAULT_HIGH := 83

LANE_COLOR := 0xFFFFFF
GRID_COLOR := 0xFFFFFF
// beats of approach over which a note glows before reaching the hit line
GLOW_BEATS := 1

HorizontalRoll := (screenHalfSize: ObservablePoint, player: Player) ->
  container := new Container

  deferCleanup := [] as (()=>)[]

  bg := new Sprite Texture.WHITE
  bg.tint = ROLL_BG_COLOR
  container.addChild bg

  // lane shading + grid + scrolling notes, redrawn every frame
  notes := new Graphics
  container.addChild notes

  playheadLine := new Sprite Texture.WHITE
  assign playheadLine,
    width: 2
    alpha: 0.75
    tint: 0x673AB7
  container.addChild playheadLine

  // the left keyboard, redrawn on layout changes only
  keys := new Graphics
  container.addChild keys

  // key highlights, redrawn every frame over the static keyboard
  dynamic := new Graphics
  container.addChild dynamic

  labels := new Container
  container.addChild labels

  // fingering labels riding on the scrolling notes (pooled, see note-fx)
  fingerPool := TextSpritePool container, fontSize: 12

  drawFingerLabel := (text: string, x: number, y: number, h: number, hand: string) ->
    return unless s := fingerPool.get text
    s.tint = FINGERING_COLORS[hand] ?? 0xFFFFFF
    s.x = floor x
    s.y = floor y + (h - s.height) / 2
    return

  // big arcade judgment popups slamming in off the hit line (arcade-fx)
  judgmentFx := JudgmentFx container, fontSize: 34

  // combo-tier hit bursts off the hit line (see note-fx.civet)
  bursts := BurstLayer container
  burstsSeenThrough .= 0

  // Lane span, always octave-aligned so the key pattern is complete
  viewLow .= DEFAULT_LOW
  viewHigh .= DEFAULT_HIGH

  // Layout, computed in layoutLanes
  laneCount .= 36
  lh .= 12 // lane (semitone) height
  keyboardWidth .= 72
  sh .= 768
  currentBeat .= 0
  // per-frame: the song's fingering annotations (if any) and tick scale
  let fingering: Record<string, string>?
  tpb .= 96

  // y extent of a note's lane; higher pitches sit higher on screen
  laneY := (n: number) ->
    sh - (clamp(n, viewLow, viewHigh) - viewLow + 1) * lh

  noteAtY := (y: number) ->
    clamp viewLow + floor((sh - y) / lh), viewLow, viewHigh

  layoutLanes := ->
    {x: shw, y: shh} := screenHalfSize
    sw := 2 * shw
    sh = 2 * shh

    laneCount = viewHigh - viewLow + 1
    lh = sh / laneCount
    keyboardWidth = clamp round(sw / 10), 48, 120

    bg.width = sw
    bg.height = sh

    playheadLine.height = sh
    playheadLine.x = keyboardWidth - 1

    keys.clear()
    labels.removeChildren().forEach (t) -> t.destroy()

    for i of [0...laneCount]
      n := viewLow + i
      y := laneY n
      // side-on piano: white lanes light, black lanes dark and inset
      if isBlack n
        keys.lineStyle 0
        keys.beginFill 0x202028
        keys.drawRect 0, y, keyboardWidth * 0.62, lh
        keys.endFill()
      else
        keys.lineStyle 1, 0x303040, 1
        keys.beginFill 0xFAFAFA
        keys.drawRect 0, y, keyboardWidth, lh
        keys.endFill()

      if n % 12 is 0 // label the Cs
        label := new Text noteName(n),
          fontFamily: "sans-serif"
          fontSize: min(12, max(8, round lh))
          fill: 0x808080
        label.x = keyboardWidth - label.width - 4
        label.y = floor y + (lh - label.height) / 2
        labels.addChild label

  layoutLanes()

  // Per-frame scratch state
  noteStartMap := new Map<number, {c: number, n: number, beats: number[]}>
  sounding := [] as [number, number][] // [note, channel]

  drawNote := (c: number, n: number, startBeat: number, endBeat: number, screenRightBeat: number) ->
    return if endBeat <= currentBeat or startBeat >= screenRightBeat

    y := laneY n
    left := keyboardWidth + max(0, startBeat - currentBeat) * PIXELS_PER_BEAT
    right := keyboardWidth + (endBeat - currentBeat) * PIXELS_PER_BEAT
    w := min(right, bg.width + PIXELS_PER_BEAT) - left

    active := startBeat <= currentBeat < endBeat
    color := CHANNEL_COLORS[c]

    // approach glow: a halo swells under the note as it nears the hit line
    approach := (startBeat - currentBeat) / GLOW_BEATS
    if 0 < approach < 1
      notes.lineStyle 0
      notes.beginFill color, 0.3 * (1 - approach)
      notes.drawRoundedRect left - 3, y - 2, w + 6, lh + 2, min(6, lh / 2 + 2)
      notes.endFill()

    if active
      notes.lineStyle 2, 0xFFFFFF, 0.9
      sounding.push [n, c]
    else
      // subtle border separates stacked notes on the dark field
      notes.lineStyle 1, 0xFFFFFF, 0.18

    notes.beginFill color, if active then 1 else 0.85
    notes.drawRoundedRect left, y + 1, w, lh - 2, min(3, (lh - 2) / 2)
    notes.endFill()

    // Fingering rides at the leading (left) edge of the scrolling note
    if fingering and w > 16
      if hint := fingering[`${round startBeat * tpb}:${c}:${n}`]
        {text, hand} := fingeringDisplay hint
        drawFingerLabel text, left + 3, y, lh, hand

  render := (song: MidiSong, _currentBeat: number) ->
    currentBeat = _currentBeat
    {ticksPerBeat, channelActive} := song
    screenRightBeat := currentBeat + (bg.width - keyboardWidth) / PIXELS_PER_BEAT

    notes.clear()
    dynamic.clear()
    sounding.length = 0
    noteStartMap.clear()
    fingering = player.practiceFingering?()
    tpb = ticksPerBeat
    // song-theme overrides (cached in the accessors)
    jc := player.practiceJudgmentColors?() ?? JUDGMENT_COLORS
    bg.tint = player.rollBackground?() ?? ROLL_BG_COLOR
    // hoisted out of the per-event walk below: practiceHidesChannel reads
    // an Observable, and per event per frame that dominates large songs
    hidden := [0...16].map (c) => player.practiceHidesChannel?.(c) ?? false

    // Black-key lanes shade darker so pitch reads across the whole field
    notes.lineStyle 0
    for i of [0...laneCount]
      n := viewLow + i
      continue unless isBlack n
      notes.beginFill LANE_COLOR, 0.03
      notes.drawRect keyboardWidth, laneY(n), bg.width - keyboardWidth, lh
      notes.endFill()

    // Grid: a faint line per beat, a stronger one per measure
    notes.lineStyle 1, GRID_COLOR, 0.08
    for b of [max(0, ceil currentBeat)...max(0, ceil screenRightBeat)]
      x := keyboardWidth + (b - currentBeat) * PIXELS_PER_BEAT
      notes.moveTo x, 0
      notes.lineTo x, sh
    notes.lineStyle 1, GRID_COLOR, 0.25
    song.measureNumbers.forEach ({beat}) ->
      return unless currentBeat <= beat < screenRightBeat
      x := keyboardWidth + (beat - currentBeat) * PIXELS_PER_BEAT
      notes.moveTo x, 0
      notes.lineTo x, sh

    minSeen .= Infinity
    maxSeen .= -Infinity

    song.events.forEach (e: MidiEvent) ->
      {type} := e
      return unless type is "noteOn" or type is "noteOff"
      {c, n} := e
      return unless channelActive[c]
      return if hidden[c]

      beat := e.tick / ticksPerBeat
      k := (c << 7) + n

      if type is "noteOn"
        minSeen = min minSeen, n
        maxSeen = max maxSeen, n
        entry .= noteStartMap.get k
        unless entry
          entry = {c, n, beats: []}
          noteStartMap.set k, entry
        entry.beats.push beat
      else
        entry := noteStartMap.get k
        return unless entry and entry.beats.length
        drawNote c, n, entry.beats.shift()!, beat, screenRightBeat

    // Notes with no matching off run to the song's end
    songBeatLength := song.beatLength()
    noteStartMap.forEach ({c, n, beats}) ->
      beats.forEach (startBeat) ->
        drawNote c, n, startBeat, songBeatLength, screenRightBeat

    // Grow (never shrink mid-song) the lanes to cover everything played;
    // the next frame draws with the new layout
    if maxSeen >= 0
      lo := floor(minSeen / 12) * 12
      hi := ceil((maxSeen + 1) / 12) * 12 - 1
      if lo < viewLow or hi > viewHigh
        viewLow = min viewLow, lo
        viewHigh = max viewHigh, hi
        layoutLanes()

    // Practice judgment feedback: flash the played key in the judgment color
    // and float the label away from the hit line as it fades
    now := performance.now()
    player.practiceFeedback?.forEach ({n, judgment, at}) ->
      age := now - at
      return if age > JUDGMENT_FEEDBACK_MS
      fade := 1 - age / JUDGMENT_FEEDBACK_MS
      y := laneY n
      dynamic.beginFill jc[judgment] ?? 0xFFFFFF, 0.7 * fade
      dynamic.drawRect 1, y + 1, keyboardWidth - 2, lh - 2
      dynamic.endFill()

    // Light the keys under sounding notes
    sounding.forEach ([n, c]) ->
      y := laneY n
      w := if isBlack n then keyboardWidth * 0.62 else keyboardWidth
      dynamic.beginFill CHANNEL_COLORS[c], 0.8
      dynamic.drawRect 1, y + 1, w - 2, lh - 2
      dynamic.endFill()

    // High-combo juice: the hit line ignites and pulses as the multiplier
    // climbs (game mode only — practice keeps a calm field)
    glow := comboGlow player, now
    if glow > 0
      dynamic.lineStyle 0
      dynamic.beginFill 0xFFFFFF, glow
      dynamic.drawRect keyboardWidth, 0, 16, sh
      dynamic.endFill()
      dynamic.beginFill 0xFFFFFF, glow * 2
      dynamic.drawRect keyboardWidth, 0, 5, sh
      dynamic.endFill()

    // game mode goes full arcade; practice keeps the same look at a calmer size
    fxScale := if player.playMode?.() is "game" then 1 else 0.6
    player.practiceFeedback?.forEach ({n, judgment, at, tier}) ->
      age := now - at
      color := jc[judgment] ?? 0xFFFFFF
      // a fresh hit not yet seen throws its sparks off the hit line, sized
      // by tier (fresh: the cursor alone would replay stale entries when
      // the view starts rendering again)
      if at > burstsSeenThrough and age < BURST_MAX_AGE_MS
        spawnJudgedBurst bursts, keyboardWidth, laneY(n) + lh / 2, color, judgment, tier
      return if age > JUDGMENT_FEEDBACK_MS
      judgmentFx.pop JUDGMENT_LABELS[judgment] ?? judgment, color, keyboardWidth + 14, laneY(n) - 12, age, {tier, judgment, scale: fxScale, anchorX: 0}
    judgmentFx.finish now
    burstsSeenThrough = now
    bursts.step now

    fingerPool.finish()

  screenPositionToBeatNote := (x: number, y: number, _event: MouseEvent) ->
    beat: currentBeat + max(0, x - keyboardWidth) / PIXELS_PER_BEAT
    note: noteAtY y

  // Tool + keyboard-preview wiring shared with the vertical roll
  rollInteraction {
    player
    container
    deferCleanup
    screenPositionToBeatNote
    keyboardNoteAt: (x: number, y: number) -> noteAtY(y) if x < keyboardWidth
  }

  container: container
  render: render

  resize: ->
    layoutLanes()

  songLoaded: (song: MidiSong) ->
    lo .= Infinity
    hi .= -Infinity
    song.events.forEach (e: MidiEvent) ->
      if e.type is "noteOn"
        {n} := e
        lo = min lo, n
        hi = max hi, n

    if hi >= 0
      viewLow = floor(lo / 12) * 12
      viewHigh = max ceil((hi + 1) / 12) * 12 - 1, viewLow + 23
    else
      viewLow = DEFAULT_LOW
      viewHigh = DEFAULT_HIGH

    layoutLanes()
    return

  screenPositionToBeatNote: screenPositionToBeatNote

  dispose: ->
    deferCleanup.forEach (fn) -> fn()

export default HorizontalRoll
